import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import { InputSwitch } from 'primereact/inputswitch'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { Calendar } from 'primereact/calendar'
import { useTranslation } from 'react-i18next'
import LayoutContainer from 'Components/LayoutComponent'
import { ButtonComponent, DialogModal } from 'Components/UIComponents'
import Loader from 'Components/Loader'
import { ROUTE_STRINGS } from 'Utils/Constants'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'

const EditICOCoffeeReport = ({ setLoading, mockEditICO, mockInitialValues }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const history = useHistory()

  const { tags: initialTag, title: initialTitle, content: initialContent, reportLink: initialReportLink, publishedDateInUTC: initialPublishedDate, isUpcoming: initialIsUpcoming, formattedDate: initialFormattedDate, idReport, monthYear } = location.state

  const [year, monthName] = monthYear.split('-').map((el) => el.trim())

  const monthIndex = new Date(`${monthName} 1, 2000`).getMonth()
  const dateObj = new Date(Number(year), monthIndex, 1)

  const [initialValues, setInitialValues] = useState({
    idReport,
    tags: initialTag,
    title: initialTitle,
    content: initialContent,
    reportLink: initialReportLink,
    publishedDate: initialPublishedDate,
    isUpcoming: initialIsUpcoming,
    formattedDate: new Date(`${initialPublishedDate}`),
    reportMonth: dateObj,
    monthYear
  })

  const { tags, title, content, reportLink, formattedDate, isUpcoming, reportMonth } = initialValues
  const [calendarEventChanged, setCalendarEventChanged] = useState(false)

  const isButtonDisabled = (!isUpcoming && (!tags || !title || !reportLink || !content)) || (isUpcoming && !calendarEventChanged && initialTag === tags)
  const [dialogState, setDialogState] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => editICOCoffeeReportData(), onDismiss: () => handleDismiss(), onSuccess: () => redirectToListICO() })

  const initialFieldDetails = [
    { type: 'calendar', fieldName: 'yearAndMonth', label: 'YEAR_AND_MONTH', dataTestId: 'year-month', errorMessage: 'YEAR_MONTH_REQUIRED', placeholder: 'YEAR_AND_MONTH', view: 'month', dateFormat: 'yy-MM', disabled: false },
    { type: 'input-text', fieldName: 'tags', label: 'TAG', dataTestId: 'tags', errorMessage: 'TAG_IS_REQUIRED', disabled: false },
    { type: 'input-text', fieldName: 'title', label: 'TITLE', dataTestId: 'title', errorMessage: 'TITLE_IS_REQUIRED', disabled: isUpcoming },
    { type: 'input-text', fieldName: 'reportLink', label: 'REPORT_LINK', dataTestId: 'reportLink', errorMessage: 'REPORT_LINK_IS_REQUIRED', disabled: isUpcoming },
    { type: 'text-area', fieldName: 'content', label: 'CONTENT', dataTestId: 'content', errorMessage: 'CONTENT_IS_REQUIRED', disabled: isUpcoming },
    { type: 'calendar', fieldName: 'publishedDate', label: 'PUBLISH_DATE', dataTestId: 'published-date', errorMessage: 'DATE_IS_REQUIRED', placeholder: 'PUBLISH_DATE', view: 'date', dateFormat: 'dd/mm/yy', disabled: false }
  ]

  const [fieldDetails, setFieldDetails] = useState(initialFieldDetails)

  // For Testing Purpose
  useEffect(() => {
    if (mockEditICO) {
      setInitialValues(mockInitialValues)
    }
  }, [])

  const editICOCoffeeReportData = async () => {
    setLoading(true)

    try {
      const { formattedDate, reportMonth, ...updatedPayload } = initialValues
      const response = await apiAdapterCoffeeWeb.updateICOCoffeeReport(updatedPayload)

      if (response.data.isSuccess) {
        setDialogState({
          ...dialogState,
          showModal: true,
          modalType: 'success',
          message: 'POST_ICO_REPORT_DATA_SUCCESS',
          onHide: () => redirectToListICO()
        })
      }
    } catch (error) {
      setDialogState({
        ...dialogState,
        showModal: true,
        modalType: 'error',
        message: 'SORRY_COULD_NOT_ADD_DATA_PLEASE_TRY_AGAIN_LATER',
        onHide: () => handleDismiss()
      })
    } finally {
      setLoading(false)
    }
  }

  const toggleButton = () => {
    setInitialValues({ ...initialValues, isUpcoming: !isUpcoming })
    setFieldDetails((prevDetails) =>
      prevDetails.map((field) => {
        if (['title', 'reportLink', 'content'].includes(field.fieldName)) {
          return { ...field, disabled: !isUpcoming }
        }

        return field
      })
    )
  }

  const validateFields = (name, value) => {
    const modifiedConfig = fieldDetails.map((ele) => (ele.fieldName === name ? { ...ele, error: value === '' } : ele))

    setFieldDetails(modifiedConfig)
  }

  const handleChange = (e) => {
    const { name, value } = e.target

    setInitialValues((prev) => ({ ...prev, [name]: value }))

    validateFields(name, value)
  }

  const handleDateChange = (event) => {
    const { value: currentDate } = event.target

    const date = new Date(currentDate)
    const year = date.getFullYear()
    const month = date.getMonth()
    const day = date.getDate()

    const utcDate = new Date(Date.UTC(year, month, day))

    const isoStringAdjusted = utcDate.toISOString()

    setInitialValues((previousData) => ({
      ...previousData,
      formattedDate: currentDate,
      publishedDate: isoStringAdjusted
    }))
  }

  const handleMonthChange = (event) => {
    const { value: currentDate } = event.target

    const date = new Date(currentDate)
    const year = date.getFullYear()
    const month = date.toLocaleString('default', { month: 'long' })

    const formattedYearMonth = `${year}-${month}`

    setInitialValues((previousData) => ({
      ...previousData,
      reportMonth: currentDate,
      monthYear: formattedYearMonth
    }))
  }

  const handleCalenderEvent = (fieldName, event) => {
    setCalendarEventChanged(true)

    if (fieldName === 'publishedDate') {
      handleDateChange(event)
    } else {
      handleMonthChange(event)
    }
  }

  const handleBlur = (e) => {
    const { name, value } = e.target

    validateFields(name, value)
  }

  const handleSubmit = () => editICOCoffeeReportData()

  const handleDismiss = () => setDialogState({ ...dialogState, showModal: false })

  const redirectToListICO = () => {
    history.push(ROUTE_STRINGS.listICOCoffeeReport)
  }

  return (
    <LayoutContainer title={'EDIT_ICO_COFFEE_REPORT'} breadCrumbsTitle={'editICOCoffeeReport'}>
      <div className="add-ico-report-container" data-testid="edit-ico-form">
        <div className="toggle-button-container">
          <label htmlFor="toggleButton" className="toggle-button">
            {t('IS_UPCOMING')}
          </label>
          <div className="input-switch-wrapper">
            <InputSwitch checked={isUpcoming} onChange={toggleButton} aria-label="is-upcoming-toggle" />
          </div>
        </div>

        {fieldDetails.map(({ fieldName, error, errorMessage, disabled, dataTestId, type, label, placeholder, view, dateFormat }, index) => (
          <div className="p-float-label field-container" key={index}>
            {type === 'input-text' && <InputText name={fieldName} id={fieldName} className={`${error && 'mandatory-field'} input-text`} value={initialValues[fieldName]} disabled={disabled} onChange={(e) => handleChange(e)} onBlur={(e) => handleBlur(e)} data-testid={dataTestId} />}
            {type === 'text-area' && <InputTextarea name={fieldName} id={fieldName} className={`${error && 'mandatory-field'} input-text-area`} value={initialValues[fieldName]} disabled={disabled} onChange={(e) => handleChange(e)} onBlur={(e) => handleBlur(e)} rows={3} placeholder={t('ENTER_REPORT_CONTENT')} data-testid={dataTestId} />}
            {type === 'calendar' && <Calendar name={fieldName} id={fieldName} placeholder={t(placeholder)} view={view} dateFormat={dateFormat} inputClassName={`${error && 'mandatory-field'} calendar-input-text`} showIcon value={fieldName === 'publishedDate' ? formattedDate : reportMonth} onChange={(e) => handleCalenderEvent(fieldName, e)} onBlur={(e) => handleBlur(e)} data-testid={dataTestId} />}
            <label className={`${error ? `${type} mandatory-field-label` : `${type} field-label`}`} htmlFor={fieldName}>
              {t(label)}
              <span className="required-field">*</span>
            </label>
            {error && <div className="error-message">{t(errorMessage)}</div>}
          </div>
        ))}

        <div className="button-container">
          <ButtonComponent type={'cancel'} onClick={() => redirectToListICO()} />
          <ButtonComponent type={'submit'} disabled={isButtonDisabled} onClick={handleSubmit} />
        </div>
      </div>
      <DialogModal {...dialogState} />
    </LayoutContainer>
  )
}

export default Loader(EditICOCoffeeReport)
